"use client";

import {
	blackFridayPricingFingerprint,
	useBlackFridayPricingSync,
} from "@/checkout/hooks/use-black-friday-pricing-sync";

import { useCallback, useEffect, useMemo, useState, useRef, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import posthog from "posthog-js";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetCloseButton } from "@/ui/components/ui/sheet";
import { ReadUnavailable } from "@/ui/components/read-unavailable";
import { useCart } from "./cart-context";
import { useCartMutation } from "./use-cart-mutation";
import { CartUpsell } from "./cart-upsell";
import { CartEssentials } from "./cart-essentials";
import { PendingCartLineItem } from "./pending-cart-line-item";
import { isPendingEntrySatisfied } from "./pending-cart-lines";
import { useAddToCart } from "./use-add-to-cart";
import { cn } from "@/lib/utils";
import { defaultLocale, formatPrice, getLocaleConfig, isValidLocale, type Locale } from "@/config/locale";
import { hasDiscount } from "@/lib/pricing";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { resolveImageAlt } from "@/lib/images/resolve-image-alt";
import { buttonStyles } from "@/ui/components/ui/button";
import { SHIPPING_CONFIG } from "@/config/shipping";
import type { AffiliatePromoCartHint } from "@/lib/affiliate/referral";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { getReferenceCurrencyForContext } from "@/lib/pricing/reference-currency-policy";
import { GEO_PREF_COOKIE, parseGeoPrefCookieValue, GEO_PREF_UPDATED_EVENT } from "@/lib/geo/geo-pref";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import { CartSettlementNotice } from "@/ui/components/pricing/cart-settlement-notice";
import { toast } from "sonner";
import { isCartBusy, resolveCartBusyState } from "@/lib/cart/cart-busy-state";
import { checkMinimumOrder } from "@/lib/cart/minimum-order";
import { resolveMerchandiseSubtotal } from "@/lib/cart/merchandise-subtotal";
import { resolveAttachedShipping } from "@/lib/cart/attached-shipping";
import { resolveFreeShippingBasis } from "@/lib/cart/discount-fold";
import { CartMovBanner } from "./cart-mov-banner";
import { resolveCheckoutGrossTotal } from "@/checkout/lib/utils/resolve-checkout-gross-total";
import { resolveCommerceSummary } from "@/lib/cart/commerce-summary";
import type { NormalizedCommerceDisplay } from "@/lib/cart/commerce-summary-adapters";
import { getPurchasableVariantDetails } from "@/lib/products/purchasable-variant";
import { parseSku } from "@/lib/stock-sync/sku-parser";
import { PaymentStrip } from "@/ui/components/pdp/payment-strip";
import {
	CART_CHECKOUT_CLICKED_EVENT,
	CART_OPENED_EVENT,
	CHECKOUT_DIAGNOSTIC_SCHEMA_VERSION,
	roundMoney2,
	totalBucket,
	type CartCheckoutBlockReason,
} from "@/lib/analytics/checkout-diagnostic-events";
import { consumeThrottled } from "@/checkout/lib/throttled-event";

interface CartLine {
	metadata?: Array<{ key: string; value: string }>;
	id: string;
	quantity: number;
	totalPrice: {
		gross: {
			amount: number;
			currency: string;
		};
	};
	variant: {
		sku?: string | null;
		id: string;
		name: string;
		product: {
			id: string;
			name: string;
			slug: string;
			translation?: { name?: string | null } | null;
			category?: { name: string; translation?: { name?: string | null } | null } | null;
			thumbnail?: {
				url: string;
				alt?: string | null;
			} | null;
		};
		pricing?: {
			price?: {
				gross: {
					amount: number;
					currency: string;
				};
			} | null;
			priceUndiscounted?: {
				gross: {
					amount: number;
					currency: string;
				};
			} | null;
		} | null;
		attributes?: Array<{
			attribute: {
				name?: string | null;
				slug?: string | null;
			};
			values: Array<{
				name?: string | null;
				value?: string | null;
			}>;
		}>;
	};
}

import { getColorHex, isColorAttribute } from "@/lib/colors";

interface VariantAttribute {
	name: string;
	value: string;
	colorHex?: string;
	isColor: boolean;
}

function getVariantDetails(variant: CartLine["variant"]): VariantAttribute[] {
	const attributes = variant.attributes || [];
	const result: VariantAttribute[] = [];

	for (const attr of attributes) {
		const slug = attr.attribute.slug || "";
		const name = attr.attribute.name || slug;
		const value = attr.values[0];

		if (!value?.name) continue;

		const isColor = isColorAttribute(slug);

		result.push({
			name,
			value: value.name,
			colorHex: isColor ? getColorHex(value) : undefined,
			isColor,
		});
	}

	// Sort: color first, then others
	return result.sort((a, b) => {
		if (a.isColor && !b.isColor) return -1;
		if (!a.isColor && b.isColor) return 1;
		return 0;
	});
}

type CartPricingResolved = {
	items: Array<{ id: string } & ResolvedMoneyPresentation>;
	totals: {
		subtotal: ResolvedMoneyPresentation;
		discount: ResolvedMoneyPresentation | null;
		total: ResolvedMoneyPresentation;
	};
	shippingThreshold: ResolvedMoneyPresentation;
	shippingFlatRate: ResolvedMoneyPresentation | null;
	remainingToFreeShipping: ResolvedMoneyPresentation | null;
};

function lineResolvedPresentation(
	item: { id: string } & ResolvedMoneyPresentation,
): ResolvedMoneyPresentation {
	const { id: _id, ...rest } = item;
	return rest;
}

/**
 * /api/pricing/resolve's zod schema requires `country` to be exactly 2 letters (or absent).
 * A misconfigured NEXT_PUBLIC_DEV_COUNTRY (e.g. "DEU", 3 letters) would otherwise fail the
 * WHOLE request body, degrading every resolved row in the drawer (subtotal, total, shipping,
 * threshold) to the channel-currency sync fallback -- not just the country-dependent part.
 * Guard at the boundary instead of trusting the upstream getCountryCodeFromHeaders() value.
 */
function isTwoLetterCountryCode(code: string | null | undefined): code is string {
	return !!code && /^[A-Z]{2}$/.test(code);
}

interface CartDrawerProps {
	/** Failed reads are not authoritative empty-cart snapshots. */
	readUnavailable?: boolean;
	commerceDisplay?: NormalizedCommerceDisplay | null;
	checkoutId: string | null;
	lines: CartLine[];
	totalPrice: {
		gross: {
			amount: number;
			currency: string;
		};
	} | null;
	/** Line subtotal (pre–checkout-level voucher); optional for older callers. */
	subtotalPrice?: {
		gross: {
			amount: number;
			currency: string;
		};
		net?: {
			amount: number;
			currency: string;
		};
	} | null;
	/** Checkout-level discount (e.g. affiliate voucher). */
	discount?: { amount: number; currency: string } | null;
	/**
	 * Saleor's real shipping price, non-zero exactly once a delivery method is attached
	 * to the checkout. From that moment `totalPrice` already contains it, so the
	 * flat-rate estimate below must stop being added on top.
	 */
	shippingPrice?: { gross: { amount: number; currency: string } } | null;
	/** `checkout.delivery` is set. Optional for older callers (tests, PDP fixtures). */
	hasDeliveryMethod?: boolean;
	affiliatePromoHint?: AffiliatePromoCartHint | null;
	channel: string;
	reviewAggregates?: Record<string, { avgRating: number; count: number }>;
	/** CF-IPCountry read server-side by CartDrawerWrapper -- an IP approximation, not the
	 *  delivery address. Feeds the reference-currency resolver explicitly instead of relying
	 *  on the same-origin fetch below to inherit the header implicitly. */
	countryCode?: string | null;
	/**
	 * Display currency pinned by the surrounding page. The US ads landing page quotes the
	 * channel settlement currency, and its own add-to-cart opens this drawer, so without
	 * the pin a non-US visitor reads dollars on the page and their own currency one click
	 * later. `null` (every other mount) keeps the `geo_pref_v2` preference.
	 */
	referenceCurrencyOverride?: string | null;
}

export function CartDrawer({
	readUnavailable = false,
	checkoutId,
	lines,
	totalPrice,
	subtotalPrice,
	discount,
	shippingPrice = null,
	hasDeliveryMethod = false,
	affiliatePromoHint,
	channel,
	countryCode = null,
	referenceCurrencyOverride = null,
	commerceDisplay,
}: CartDrawerProps) {
	const cart = useCart();
	const {
		isOpen,
		closeCart,
		pendingLines,
		syncAuthoritativeCartLines,
		knownCheckout,
		syncMutationSnapshot,
		acquireMutation,
		releaseMutation,
		mutationLocked,
		upgradePending,
		setUpgradePending,
	} = cart;
	const addToCart = useAddToCart();
	const router = useRouter();
	const refreshBlackFridayPricing = useCallback(() => router.refresh(), [router]);
	useBlackFridayPricingSync(checkoutId, blackFridayPricingFingerprint({ lines }), refreshBlackFridayPricing, {
		syncOnMount: false,
	});
	const { locale } = useParams<{ locale: string }>();
	const t = useTranslations("cart");
	const common = useTranslations("common");
	const footerLabels = useTranslations("footer");
	const [pricingResolved, setPricingResolved] = useState<{ key: string; value: CartPricingResolved } | null>(
		null,
	);
	const [geoPrefTick, setGeoPrefTick] = useState(0);
	const [compactFooter, setCompactFooter] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const openerRef = useRef<HTMLElement | null>(null);
	const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
	const attachContent = useCallback((element: HTMLDivElement | null) => {
		contentRef.current = element;
		setContentElement(element);
	}, []);
	const headerRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);
	const detailsRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const pendingFocusRef = useRef<string | null>(null);
	const {
		mutate,
		retry,
		checkStatus,
		error: mutationError,
		isPending,
	} = useCartMutation({
		checkoutId,
		onDeleteConfirmed: () => listRef.current?.focus(),
	});

	useEffect(() => {
		const bump = () => setGeoPrefTick((n) => n + 1);
		window.addEventListener(GEO_PREF_UPDATED_EVENT, bump);
		return () => window.removeEventListener(GEO_PREF_UPDATED_EVENT, bump);
	}, []);
	const authoritativeLines = useMemo(
		() => lines.map((line) => ({ id: line.id, variantId: line.variant.id, quantity: line.quantity })),
		[lines],
	);
	const transientLines = useMemo(
		() => pendingLines.filter((entry) => !isPendingEntrySatisfied(entry, authoritativeLines)),
		[pendingLines, authoritativeLines],
	);
	useEffect(() => {
		if (readUnavailable) return;
		syncAuthoritativeCartLines(authoritativeLines);
		syncMutationSnapshot?.(authoritativeLines);
	}, [authoritativeLines, syncAuthoritativeCartLines, syncMutationSnapshot, readUnavailable]);
	useEffect(() => {
		if (!isOpen || !pendingFocusRef.current) return;
		const target = contentRef.current?.querySelector<HTMLElement>(
			`[data-cart-line="${CSS.escape(pendingFocusRef.current)}"] button:not(:disabled)`,
		);
		if (target) {
			target.focus();
			if (document.activeElement === target) pendingFocusRef.current = null;
		}
	}, [lines, isOpen, mutationLocked, upgradePending, isPending]);

	useEffect(() => {
		if (!isOpen || !contentElement || typeof ResizeObserver === "undefined") return;
		// Measure the full natural footer even when its details live in the scroller.
		const measure = () => {
			const height = contentRef.current?.getBoundingClientRect().height ?? window.innerHeight;
			const header = headerRef.current?.getBoundingClientRect().height ?? 0;
			const footer = footerRef.current?.getBoundingClientRect().height ?? 0;
			const details = detailsRef.current?.getBoundingClientRect().height ?? 0;
			const available = height - header - footer - (compactFooter ? details : 0);
			setCompactFooter((current) => available < (current ? 144 : 120));
		};
		const observer = new ResizeObserver(measure);
		[contentRef.current, headerRef.current, footerRef.current, detailsRef.current].forEach(
			(element) => element && observer.observe(element),
		);
		window.addEventListener("resize", measure);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [isOpen, compactFooter, contentElement, lines.length]);

	const loc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;
	const fmt = (amount: number, cur: string) => formatPrice(amount, cur, loc);
	const currency =
		totalPrice?.gross.currency ??
		subtotalPrice?.gross.currency ??
		(channel === "us" ? "USD" : getLocaleConfig(defaultLocale).fallbackCurrency);
	// Compatibility for isolated callers; the server wrapper always supplies the normalized snapshot.
	const legacyDisplay = useMemo<NormalizedCommerceDisplay>(() => {
		const subtotal = resolveMerchandiseSubtotal(
			subtotalPrice,
			lines.reduce((sum, line) => sum + line.totalPrice.gross.amount, 0),
		);
		const regular = Object.fromEntries(
			lines.map((line) => [
				line.id,
				line.variant.pricing?.price
					? line.variant.pricing.price.gross.amount * line.quantity
					: line.totalPrice.gross.amount,
			]),
		);
		const folded = lines.reduce(
			(sum, line) => sum + Math.max(0, regular[line.id]! - line.totalPrice.gross.amount),
			0,
		);
		const attached = resolveAttachedShipping({
			hasDeliveryMethod,
			shippingPriceAmount: shippingPrice?.gross.amount,
		});
		const discountAmount = discount?.amount ?? 0;
		return {
			lineAmountById: regular,
			summaryInput: {
				scope: "checkout",
				channel,
				currency,
				subtotalAmount: Object.values(regular).reduce((a, b) => a + b, 0),
				discountAmount,
				resolvedTotalAmount: resolveCheckoutGrossTotal({
					subtotal,
					shipping: attached.amount,
					discount: discountAmount,
					apiTotal: totalPrice?.gross.amount ?? subtotal,
					lineDiscountInPrices: folded,
				}),
				freeShippingBasisAmount: resolveFreeShippingBasis(subtotal, discountAmount, folded),
				attached,
				delivery: {
					status: hasDeliveryMethod ? "confirmed" : "unresolved",
					amountIsKnown: shippingPrice?.gross.amount != null,
					appliesToCurrentCart: hasDeliveryMethod,
					shippingRequired: true,
				},
				freshness: "ready",
			},
		};
	}, [lines, subtotalPrice, totalPrice, shippingPrice, discount, hasDeliveryMethod, channel, currency]);
	const display = commerceDisplay ?? legacyDisplay;
	// Stable props: both widgets refetch product data whenever `lines` changes identity,
	// and an inline `.map()` gave them a new array on every drawer render.
	const upsellLines = useMemo(
		() =>
			lines.map((line) => ({
				id: line.id,
				quantity: line.quantity,
				metadata: line.metadata,
				variantId: line.variant.id,
				variantName: line.variant.name,
				productSlug: line.variant.product.slug,
				productName: line.variant.product.translation?.name ?? line.variant.product.name,
				thumbnailUrl: line.variant.product.thumbnail?.url ?? undefined,
				totalPrice: display.lineAmountById[line.id] ?? line.totalPrice.gross.amount,
			})),
		[lines, display.lineAmountById],
	);
	const essentialLines = useMemo(
		() =>
			lines.map((line) => ({
				productSlug: line.variant.product.slug,
				categoryName: line.variant.product.category?.name ?? null,
			})),
		[lines],
	);
	/**
	 * Three states, not one boolean — see `resolveCartBusyState`. `busy` is kept as the
	 * exact historical value (pinned by an equivalence test) so everything downstream that
	 * has not deliberately opted into the finer distinction is bit-for-bit unchanged,
	 * `summary` included.
	 */
	const busyState = resolveCartBusyState({
		mutationInFlight: Boolean(isPending || mutationLocked || upgradePending),
		transientLines,
	});
	const busy = isCartBusy(busyState);
	/** A write is in flight whose outcome nobody knows yet. */
	const unsettled = busyState.kind === "unsettled";
	/** Every add is Saleor-confirmed; only our own re-render is outstanding. */
	const settling = busyState.kind === "settling";
	/**
	 * True once the rendered props are trustworthy. Until then every money slot renders a
	 * placeholder instead of a number, because `resolveCommerceSummary` passes the PRE-ADD
	 * `subtotalAmount` and `discountAmount` through its early return verbatim — i.e. totals
	 * that omit the item the visitor just added.
	 */
	const settledView = !readUnavailable && busyState.kind === "ready";
	/**
	 * The footer (totals + checkout CTA) and the promo/MOV banner were gated on
	 * `lines.length > 0` alone. On a cold visit the FIRST add therefore rendered no footer
	 * and no checkout button at all until `router.refresh()` landed — precisely the visitor
	 * the ads landing page exists for.
	 *
	 * A FAILED row is excluded on purpose: it is not a cart, and counting it would render a
	 * confident "Subtotal 0.00" next to a live checkout button for an empty checkout.
	 */
	/**
	 * Props id wins; the add-response id only fills the cold-start hole. Used for the
	 * checkout link ONLY — deliberately NOT for the `{checkoutId && …}` cross-sell gates
	 * further down, which render off props `lines` that do not exist yet.
	 */
	const effectiveCheckoutId =
		checkoutId ?? (knownCheckout?.channel === channel ? knownCheckout.checkoutId : null);
	const hasCartContent =
		!readUnavailable && (lines.length > 0 || transientLines.some((line) => line.status !== "failed"));
	const summary = resolveCommerceSummary({
		...display.summaryInput,
		freshness: busy ? "updating" : commerceDisplay === null ? "unavailable" : display.summaryInput.freshness,
	});
	const mov = checkMinimumOrder(display.summaryInput.freeShippingBasisAmount, channel);
	const totalLabel =
		summary.totalKind === "estimated"
			? t("estimatedTotal")
			: summary.totalKind === "confirmed"
				? t("total")
				: t("totalExcludingShipping");
	const isEstimate = summary.totalKind === "estimated";
	const totalAmount = summary.totalAmount;

	const pricingPayload = useMemo(() => {
		void geoPrefTick;
		if (!lines.length) return null;
		const cookie =
			typeof document === "undefined"
				? undefined
				: document.cookie.match(new RegExp(`(?:^|; )${GEO_PREF_COOKIE}=([^;]*)`))?.[1];
		return {
			locale,
			channel,
			country: isTwoLetterCountryCode(countryCode) ? countryCode : null,
			referenceCurrency:
				referenceCurrencyOverride ??
				parseGeoPrefCookieValue(cookie ? decodeURIComponent(cookie) : undefined)?.referenceCurrency ??
				null,
			items: [
				...lines.map((line) => ({
					id: line.id,
					amount: display.lineAmountById[line.id] ?? line.totalPrice.gross.amount,
					currency,
				})),
				{ id: "cart-mov-threshold", amount: mov.threshold, currency },
				{ id: "cart-mov-shortfall", amount: mov.shortfall, currency },
			],
			totals: {
				subtotal: { amount: summary.subtotalAmount, currency },
				...(summary.discountAmount > 0 ? { discount: { amount: summary.discountAmount, currency } } : {}),
				// The formatter requires a total; its placeholder is never rendered when
				// unknown. Keep resolving the independently known line/reference prices.
				total: { amount: totalAmount ?? 0, currency },
			},
			shippingThreshold: { amount: summary.freeShipping.threshold, currency },
			shippingFlatRate: { amount: summary.shipping.amount ?? 0, currency },
			...(summary.freeShipping.remaining > 0
				? { remainingToFreeShipping: { amount: summary.freeShipping.remaining, currency } }
				: {}),
		};
	}, [
		mov.threshold,
		mov.shortfall,
		geoPrefTick,
		lines,
		display.lineAmountById,
		locale,
		channel,
		countryCode,
		referenceCurrencyOverride,
		currency,
		summary.subtotalAmount,
		summary.discountAmount,
		totalAmount,
		summary.freeShipping.threshold,
		summary.freeShipping.remaining,
		summary.shipping.amount,
	]);
	const priceKey = JSON.stringify(pricingPayload);
	const pricing = isOpen && pricingResolved?.key === priceKey ? pricingResolved.value : null;
	// Same inputs as the resolve request above, so the settlement notice follows the currency
	// the drawer displays -- and is already there while that request is still in flight.
	const referenceCurrency = pricingPayload
		? getReferenceCurrencyForContext({
				countryCode: pricingPayload.country,
				locale: loc,
				channel,
				referenceCurrencyOverride: pricingPayload.referenceCurrency,
			})
		: undefined;
	const movThresholdLabel =
		pricing?.items.find((item) => item.id === "cart-mov-threshold")?.primaryLabel ??
		fmt(mov.threshold, currency);
	const movShortfallLabel =
		pricing?.items.find((item) => item.id === "cart-mov-shortfall")?.primaryLabel ??
		fmt(mov.shortfall, currency);
	/**
	 * `busy` deliberately does NOT appear here.
	 *
	 * It stays true for the whole `settling` window, so reusing it would re-disable the
	 * checkout button at the exact moment the cart became provably correct — the single
	 * most valuable second in the funnel, spent on a dead button.
	 *
	 * Why `settling` carries no minimum-order gate: `mov` is derived from
	 * `freeShippingBasisAmount`, which during `settling` is still the PRE-ADD basis. An add
	 * can only ever RAISE the subtotal, so a stale basis can only ever produce a false
	 * "not met" — never a false "met". Gating on it could therefore only wrongly block a
	 * visitor whose add just pushed them over the threshold. The real gate is enforced
	 * server-side anyway: `checkout/page.tsx:102-104` redirects back to the cart with
	 * `?reason=minimum-order`, which raises the same toast.
	 */
	const disabledReason = unsettled
		? t("totalsUpdating")
		: settling
			? // Structural guard, not a live case today: without a checkout target the anchor
				// would render enabled-looking, swallow the click in `preventDefault` and show
				// nothing, because the toast is tied to `disabledReason`. Falling back keeps the
				// honest reason + toast for any settling row that has no target yet.
				effectiveCheckoutId
				? undefined
				: t("totalsUpdating")
			: summary.totalKind === "unavailable" || summary.freshness !== "ready"
				? t("checkoutUnavailable")
				: !mov.isMet
					? t("minimumOrderToast", { threshold: movThresholdLabel, amount: movShortfallLabel })
					: undefined;
	/** `disabledReason` branch for branch, as a stable code for analytics instead of translated text. */
	const disabledCode: CartCheckoutBlockReason | undefined = unsettled
		? "totals_updating"
		: settling
			? effectiveCheckoutId
				? undefined
				: "totals_updating"
			: summary.totalKind === "unavailable" || summary.freshness !== "ready"
				? "checkout_unavailable"
				: !mov.isMet
					? "minimum_order_not_met"
					: undefined;
	/**
	 * `cart_checkout_clicked`: the only trace of this CTA now that autocapture carries no
	 * href. Amounts are the channel currency and, like the footer, only sent once the view
	 * is settled: before that `summary` still holds the pre-add totals (see `settledView`).
	 * Counts include the live transient rows the visitor sees. A navigating click goes out
	 * instantly, the page unloads next; repeated taps on a blocked button collapse into one
	 * event per reason and 10 s. Never throws: analytics must not break the checkout click.
	 */
	const captureCheckoutClick = (blockedReason: CartCheckoutBlockReason | null) => {
		try {
			const gate = blockedReason
				? consumeThrottled(`${CART_CHECKOUT_CLICKED_EVENT}:drawer:${blockedReason}`, 10_000)
				: { emit: true, suppressed: 0 };
			if (!gate.emit) return;
			const cartValue = settledView ? roundMoney2(summary.totalAmount ?? summary.subtotalAmount) : null;
			const liveRows = transientLines.filter((entry) => entry.status !== "failed");
			// An add of a variant that is already a line raises its quantity, not the line count.
			const newVariants = new Set(
				liveRows
					.filter((entry) => !lines.some((line) => line.variant.id === entry.variantId))
					.map((entry) => entry.variantId),
			);
			posthog.capture(
				CART_CHECKOUT_CLICKED_EVENT,
				{
					event_schema_version: CHECKOUT_DIAGNOSTIC_SCHEMA_VERSION,
					source: "drawer",
					checkout_id: effectiveCheckoutId,
					channel,
					locale: loc,
					cart_value: cartValue,
					value_kind: settledView ? summary.totalKind : "unavailable",
					currency,
					total_bucket: totalBucket(cartValue),
					lines_count: lines.length + newVariants.size,
					items_count:
						lines.reduce((sum, line) => sum + line.quantity, 0) +
						liveRows.reduce((sum, entry) => sum + entry.quantity, 0),
					blocked: blockedReason !== null,
					blocked_reason: blockedReason,
					suppressed_count: gate.suppressed,
				},
				blockedReason ? undefined : { send_instantly: true },
			);
		} catch {
			// Analytics must never break the checkout click.
		}
	};
	/**
	 * `cart_opened`, once per opening (the rising edge of `isOpen`), with the trigger the
	 * opener named and the cart as the render that opened the drawer shows it. Counts
	 * include the live transient rows, like `cart_checkout_clicked`; the total goes out
	 * only as a band of the channel-currency amount and only once the view is settled,
	 * otherwise `unknown` (an add-to-cart opening is usually still settling).
	 */
	const openedRef = useRef(false);
	useEffect(() => {
		if (!isOpen) {
			openedRef.current = false;
			return;
		}
		if (openedRef.current) return;
		openedRef.current = true;
		if (readUnavailable) return;
		try {
			const liveRows = transientLines.filter((entry) => entry.status !== "failed");
			const newVariants = new Set(
				liveRows
					.filter((entry) => !lines.some((line) => line.variant.id === entry.variantId))
					.map((entry) => entry.variantId),
			);
			posthog.capture(CART_OPENED_EVENT, {
				event_schema_version: CHECKOUT_DIAGNOSTIC_SCHEMA_VERSION,
				trigger: cart.consumeOpenTrigger?.() ?? "other",
				checkout_id: effectiveCheckoutId,
				channel,
				lines_count: lines.length + newVariants.size,
				items_count:
					lines.reduce((sum, line) => sum + line.quantity, 0) +
					liveRows.reduce((sum, entry) => sum + entry.quantity, 0),
				total_bucket: totalBucket(
					settledView ? roundMoney2(summary.totalAmount ?? summary.subtotalAmount) : null,
				),
			});
		} catch {
			// Analytics must never break the drawer.
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening: the snapshot of the render that opened the drawer, not a new event on every later cart change
	}, [isOpen]);
	useEffect(() => {
		if (!isOpen || readUnavailable || !pricingPayload) return;
		const controller = new AbortController();
		void (async () => {
			try {
				const response = await fetch("/api/pricing/resolve", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: priceKey,
					signal: controller.signal,
				});
				if (!response.ok) return;
				const value = (await response.json()) as CartPricingResolved;
				if (!controller.signal.aborted) setPricingResolved({ key: priceKey, value });
			} catch {
				/* All rows use the same channel-currency fallback. */
			}
		})();
		return () => controller.abort();
	}, [isOpen, priceKey, pricingPayload, readUnavailable]);
	/**
	 * Every money slot goes through here. While the props are behind, the drawer shows a
	 * placeholder rather than a number: `resolveCommerceSummary` passes the PRE-ADD
	 * `subtotalAmount`/`discountAmount` through its early return unchanged, so rendering
	 * them would state a total that omits the item just added. A placeholder is honest;
	 * a confident stale number is not.
	 */
	const moneySlot = (node: ReactNode) =>
		settledView ? (
			node
		) : (
			<span
				aria-hidden
				data-testid="cart-money-pending"
				className="inline-block h-4 w-16 animate-pulse rounded bg-border align-middle motion-reduce:animate-none"
			/>
		);
	const money = (amount: number, presentation?: ResolvedMoneyPresentation | null, large = false) =>
		presentation ? (
			<ReferencePrimaryPrice
				presentation={presentation}
				locale={locale}
				channel={channel}
				size={large ? "md" : "sm"}
				showReferenceTooltip={large}
			/>
		) : (
			fmt(amount, currency)
		);
	const shippingText =
		summary.shipping.kind === "estimated-free"
			? t("shippingEstimatedFree")
			: summary.shipping.kind === "confirmed-free"
				? t("shippingFree")
				: summary.shipping.kind === "not-required"
					? t("shippingNotRequired")
					: summary.shipping.amount === null
						? t(
								summary.shipping.kind === "updating"
									? "shippingUpdating"
									: summary.shipping.kind === "failed"
										? "shippingFailed"
										: "shippingUnavailable",
							)
						: null;
	const progressText =
		summary.freeShipping.message === "remaining"
			? t("freeShippingProgress", {
					amount:
						pricing?.remainingToFreeShipping?.primaryLabel ?? fmt(summary.freeShipping.remaining, currency),
				})
			: summary.freeShipping.message === "estimated-qualified"
				? t("freeShippingEstimatedQualified")
				: t("freeShippingQualified");
	const navigation = (
		<div className="mt-1 flex flex-wrap items-center justify-center gap-x-4 text-xs">
			<button
				type="button"
				onClick={closeCart}
				className="min-h-11 text-muted-foreground hover:text-foreground"
			>
				{t("continueShopping")}
			</button>
			<Link
				href={`/${locale}/${channel}/cart`}
				onClick={closeCart}
				className="flex min-h-11 items-center underline underline-offset-4"
			>
				{t("viewCart")}
			</Link>
		</div>
	);
	const subtotalRow = (
		<div className="flex justify-between gap-4">
			<span className="text-muted-foreground">{t("subtotal")}</span>
			<span className="text-right tabular-nums">
				{moneySlot(money(summary.subtotalAmount, pricing?.totals.subtotal))}
			</span>
		</div>
	);
	const caveats = (
		<>
			{isEstimate && (
				<p className="text-xs leading-relaxed text-muted-foreground">{t("shippingCountryDisclaimer")}</p>
			)}
			<CartSettlementNotice
				channel={channel}
				showDual={pricing?.totals.total.showDual ?? false}
				referenceCurrency={referenceCurrency}
				className="border-0 px-0 py-0"
			/>
		</>
	);
	const compactExtras = (
		<div ref={detailsRef} className="space-y-3 border-t border-border px-5 py-4 text-sm">
			{subtotalRow}
			{caveats}
			{navigation}
		</div>
	);
	const details = (
		<div className={cn("space-y-2 px-5 text-sm", compactFooter ? "py-2" : "py-4")}>
			{!compactFooter && subtotalRow}
			{summary.shipping.kind !== "not-required" && (
				<div className="flex justify-between gap-4">
					<span className="text-muted-foreground">
						{isEstimate ? t("shippingEstimateLabel") : t("shipping")}
					</span>
					<span className="text-right tabular-nums">
						{moneySlot(shippingText ?? money(summary.shipping.amount ?? 0, pricing?.shippingFlatRate))}
					</span>
				</div>
			)}
			<div
				aria-live="polite"
				aria-atomic="true"
				className="flex items-start justify-between gap-4 border-t border-border pt-3 text-base font-semibold"
			>
				<span>{totalLabel}</span>
				<span className="shrink-0 text-right tabular-nums">
					{moneySlot(
						totalAmount === null ? t("shippingUnavailable") : money(totalAmount, pricing?.totals.total, true),
					)}
				</span>
			</div>
			{summary.discountAmount > 0 && (
				<div className="flex justify-between gap-3 text-xs text-success">
					<span>{t("discountIncluded")}</span>
					<span className="text-right tabular-nums">
						{moneySlot(<>-{money(summary.discountAmount, pricing?.totals.discount)}</>)}
					</span>
				</div>
			)}
			{!compactFooter && caveats}
		</div>
	);

	return (
		<Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
			<SheetContent
				ref={attachContent}
				onOpenAutoFocus={() => {
					const active = document.activeElement;
					openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
				}}
				onCloseAutoFocus={(event) => {
					event.preventDefault();
					const opener = openerRef.current;
					openerRef.current = null;
					if (
						opener?.isConnected &&
						opener.tabIndex >= 0 &&
						!opener.matches(":disabled") &&
						!opener.closest("[inert], [hidden]") &&
						(document.activeElement === document.body || contentRef.current?.contains(document.activeElement))
					)
						opener.focus({ preventScroll: true });
				}}
				onClickCapture={(event) => {
					// Navigation owns the next page's focus; blocked checkout links still return to the opener.
					const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
					if (link && link.getAttribute("aria-disabled") !== "true") openerRef.current = null;
				}}
				side="right"
				aria-describedby={undefined}
				className="flex h-dvh flex-col gap-0 p-0 sm:max-w-md"
			>
				<div ref={headerRef} className="shrink-0">
					<SheetHeader className="justify-between border-b border-border px-5 py-3">
						<div className="flex items-center gap-3">
							<ShoppingBag className="h-5 w-5" aria-hidden />
							<SheetTitle className="text-lg">{t("yourBag")}</SheetTitle>
						</div>
						<SheetCloseButton
							closeLabel={common("close")}
							className="static flex h-11 w-11 items-center justify-center"
						/>
					</SheetHeader>
					{hasCartContent && (
						<div className="border-b border-border px-5 py-4">
							{affiliatePromoHint && (
								<p className="mb-2 text-xs text-muted-foreground">
									{affiliatePromoHint.kind === "PERCENTAGE"
										? t("affiliateDiscountPercentWillApply", { percent: affiliatePromoHint.value })
										: affiliatePromoHint.kind === "FIXED"
											? t("affiliateDiscountFixedWillApply", {
													amount: fmt(affiliatePromoHint.amount, affiliatePromoHint.currency),
												})
											: t("affiliateDiscountGenericWillApply")}
								</p>
							)}
							{unsettled ? (
								<p role="status" className="text-sm text-muted-foreground">
									{t("totalsUpdating")}
								</p>
							) : settling ? (
								// The add is confirmed; only the totals are outstanding. Saying the cart
								// is still updating here contradicts the page, which has already flipped
								// its button to "In your cart".
								<p role="status" className="text-sm text-muted-foreground">
									{t("addedCalculatingTotal")}
								</p>
							) : !mov.isMet ? (
								<CartMovBanner
									shortfallLabel={movShortfallLabel}
									thresholdLabel={movThresholdLabel}
									progress={
										mov.threshold > 0 ? display.summaryInput.freeShippingBasisAmount / mov.threshold : 1
									}
								/>
							) : (
								summary.freeShipping.message !== "hidden" && (
									<div className="space-y-2">
										<p className="flex items-start gap-2 text-sm leading-relaxed">
											<Truck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
											<span>{progressText}</span>
										</p>
										<div
											role="progressbar"
											aria-label={progressText}
											aria-valuenow={summary.freeShipping.percent}
											aria-valuemin={0}
											aria-valuemax={100}
											className="h-1 overflow-hidden rounded-full bg-secondary"
										>
											<div
												className="h-full rounded-full bg-primary transition-[width] motion-reduce:transition-none"
												style={{ width: `${summary.freeShipping.percent}%` }}
											/>
										</div>
									</div>
								)
							)}
						</div>
					)}
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
					{readUnavailable ? (
						<div className="px-5 py-10">
							<ReadUnavailable />
						</div>
					) : lines.length === 0 && transientLines.length === 0 ? (
						<div className="flex min-h-64 flex-col items-center justify-center px-5 py-10 text-center">
							<ShoppingBag className="mb-5 h-10 w-10 text-muted-foreground" />
							<h3 className="text-base font-medium">{t("empty")}</h3>
							<p className="mb-5 mt-2 text-sm text-muted-foreground">{t("emptyDescription")}</p>
							<Link
								href={`/${locale}/${channel}/products`}
								onClick={closeCart}
								className={buttonStyles({ className: "min-h-11" })}
							>
								{t("startShopping")}
							</Link>
						</div>
					) : (
						<>
							<ul
								ref={listRef}
								tabIndex={-1}
								aria-label={t("title")}
								className="divide-y divide-border outline-none"
							>
								{lines.map((line) => {
									const productName = line.variant.product.translation?.name ?? line.variant.product.name;
									const href = `/${locale}/${channel}/products/${line.variant.product.slug}?variant=${line.variant.id}`;
									const thumbnail = normalizeSaleorMediaUrl(line.variant.product.thumbnail?.url);
									const attrs = getVariantDetails(line.variant);
									const pack = line.variant.sku ? parseSku(line.variant.sku) : null;
									const vialPack = pack?.baseSku.endsWith("-1VIAL") ? pack : null;
									const resolvedLine = pricing?.items.find((item) => item.id === line.id);
									return (
										<li key={line.id} data-cart-line={line.id} className="px-5 py-5">
											<div className="flex gap-3">
												<Link
													href={href}
													aria-label={productName}
													onClick={closeCart}
													className="relative h-24 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary"
												>
													{thumbnail ? (
														<Image
															src={thumbnail}
															alt={resolveImageAlt(line.variant.product.thumbnail?.alt, productName)}
															fill
															sizes="64px"
															className="object-contain"
														/>
													) : (
														<ShoppingBag className="m-auto mt-8 h-6 w-6 text-muted-foreground" />
													)}
												</Link>
												<div className="min-w-0 flex-1">
													<div className="flex items-start justify-between gap-1">
														<div className="min-w-0">
															<Link
																href={href}
																onClick={closeCart}
																className="text-sm font-semibold leading-relaxed hover:underline"
															>
																{productName}
															</Link>
															<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
																{attrs.length
																	? attrs.map((attr) => `${attr.name} ${attr.value}`).join(" · ")
																	: vialPack
																		? getPurchasableVariantDetails(line.variant).doseLabel
																		: line.variant.name}
															</p>
															{vialPack && (
																<p className="mt-1 text-xs text-muted-foreground">
																	{t("packContents", { count: vialPack.packCount })}
																	{line.quantity > 1 && (
																		<> · {t("totalVials", { count: vialPack.packCount * line.quantity })}</>
																	)}
																</p>
															)}
														</div>
														<button
															type="button"
															onClick={() => mutate(line.id, null)}
															disabled={busy}
															className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
															aria-label={t("remove", { product: productName })}
														>
															<Trash2 className="h-4 w-4" />
														</button>
													</div>
													<div className="mt-3 flex flex-wrap items-end justify-between gap-3">
														<div>
															<p className="mb-1 text-xs text-muted-foreground">{t("packQuantity")}</p>
															<div className="flex w-fit items-center rounded-lg border border-border">
																<button
																	type="button"
																	disabled={busy || line.quantity <= 1}
																	onClick={() => mutate(line.id, line.quantity - 1)}
																	className="flex h-11 w-11 items-center justify-center rounded-l-lg hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
																	aria-label={`${t("decreaseQuantity")}: ${productName}`}
																>
																	<Minus className="h-3.5 w-3.5" />
																</button>
																<span className="w-6 text-center text-sm tabular-nums">{line.quantity}</span>
																<button
																	type="button"
																	disabled={busy}
																	onClick={() => mutate(line.id, line.quantity + 1)}
																	className="flex h-11 w-11 items-center justify-center rounded-r-lg hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
																	aria-label={`${t("increaseQuantity")}: ${productName}`}
																>
																	<Plus className="h-3.5 w-3.5" />
																</button>
															</div>
														</div>
														<div className="text-right text-sm font-semibold tabular-nums">
															{money(
																display.lineAmountById[line.id] ?? line.totalPrice.gross.amount,
																resolvedLine ? lineResolvedPresentation(resolvedLine) : null,
															)}
															{hasDiscount(
																line.variant.pricing?.price?.gross.amount,
																line.variant.pricing?.priceUndiscounted?.gross.amount,
															) &&
																!pricing?.totals.total.showDual && (
																	<span className="block text-xs font-normal text-muted-foreground line-through">
																		{fmt(
																			(line.variant.pricing?.priceUndiscounted?.gross.amount ?? 0) *
																				line.quantity,
																			currency,
																		)}
																	</span>
																)}
														</div>
													</div>
												</div>
											</div>
										</li>
									);
								})}
								{transientLines.map((line) => (
									<PendingCartLineItem
										key={line.chainId}
										line={line}
										onRetry={() => void addToCart(line.request, { replaceAttemptId: line.attemptId })}
									/>
								))}
							</ul>
							{mutationError && (
								<div role="alert" className="mx-5 mb-4 rounded-lg border border-destructive p-3 text-sm">
									<p>{t("changeFailed")}</p>
									<button
										type="button"
										onClick={retry}
										disabled={isPending || upgradePending}
										className="min-h-11 underline underline-offset-4"
									>
										{t("retryChange")}
									</button>
									{mutationError.status === "unknown" && (
										<button
											type="button"
											onClick={checkStatus}
											disabled={isPending}
											className="ml-4 min-h-11 underline underline-offset-4"
										>
											{t("packCheck")}
										</button>
									)}
								</div>
							)}
							{checkoutId && (
								<CartUpsell
									lines={upsellLines}
									subtotal={summary.subtotalAmount}
									checkoutId={checkoutId}
									channel={channel}
									locale={locale}
									mutationsDisabled={Boolean(isPending || mutationLocked || transientLines.length)}
									acquireMutation={acquireMutation}
									releaseMutation={releaseMutation}
									onUpgradePendingChange={setUpgradePending}
									referenceCurrencyOverride={referenceCurrencyOverride}
									onCartChanged={() => router.refresh()}
									onUpgradeComplete={(lineId) => {
										pendingFocusRef.current = lineId;
										const target = contentRef.current?.querySelector<HTMLElement>(
											`[data-cart-line="${CSS.escape(lineId)}"] button:not(:disabled)`,
										);
										if (target) {
											target.focus();
											if (document.activeElement === target) pendingFocusRef.current = null;
										}
									}}
								/>
							)}
							{checkoutId && (
								<div inert={busy || undefined}>
									<CartEssentials
										lines={essentialLines}
										checkoutId={checkoutId}
										channel={channel}
										locale={locale}
										referenceCurrencyOverride={referenceCurrencyOverride}
									/>
								</div>
							)}
							<div className="space-y-3 px-5 py-5">
								<Link
									href={`/${locale}/${channel}/shipping`}
									onClick={closeCart}
									className="flex min-h-11 items-center justify-center text-xs underline underline-offset-4"
								>
									{footerLabels("shippingLink")}
								</Link>
								<PaymentStrip
									channel={channel}
									countryCode={countryCode ?? undefined}
									layout="grid"
									compact
								/>
								<p className="text-center text-xs text-muted-foreground">
									{t("shippingDeliveryWindow", {
										min: SHIPPING_CONFIG.deliveryDays.min,
										max: SHIPPING_CONFIG.deliveryDays.max,
									})}
								</p>
							</div>
						</>
					)}
					{hasCartContent && compactFooter && compactExtras}
				</div>
				{hasCartContent && (
					<div
						ref={footerRef}
						className="shrink-0 border-t border-border bg-background pb-[env(safe-area-inset-bottom)]"
					>
						{details}
						<div className={cn("px-5 pb-3", compactFooter && "pt-3")}>
							{disabledReason && <p className="mb-2 text-xs text-muted-foreground">{disabledReason}</p>}
							<a
								href={`/${locale}/checkout?checkout=${effectiveCheckoutId}`}
								aria-disabled={Boolean(disabledReason || !effectiveCheckoutId)}
								onClick={(event) => {
									if (disabledReason || !effectiveCheckoutId) {
										event.preventDefault();
										captureCheckoutClick(disabledCode ?? "no_checkout");
										if (disabledReason) toast.error(disabledReason);
									} else {
										captureCheckoutClick(null);
										closeCart();
									}
								}}
								className={buttonStyles({
									className:
										"group h-auto min-h-12 w-full whitespace-normal px-4 py-3 text-sm aria-disabled:cursor-not-allowed aria-disabled:opacity-60",
								})}
							>
								<span>{t("checkout")}</span>
								<ArrowRight className="ml-2 h-4 w-4 shrink-0" />
							</a>
							{!compactFooter && navigation}
						</div>
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
}
